/**
 * Design Tokens - Centralized design values
 * Use these tokens throughout the application for consistency
 * Updated: May 2026 - Premium Enterprise Design System
 */

export const tokens = {
  colors: {
    primary: {
      50: '#EFF6FF',
      100: '#DBEAFE',
      200: '#BFDBFE',
      300: '#93C5FD',
      400: '#60A5FA',
      main: '#3B82F6',     // Professional Blue
      500: '#3B82F6',
      600: '#2563EB',
      700: '#1D4ED8',
      800: '#1E40AF',
      900: '#1E3A8A',
      light: '#60A5FA',
      dark: '#1D4ED8',
      surface: '#EFF6FF',
      hover: '#2563EB',
    },
    secondary: {
      main: '#0891B2',     // Professional Cyan
      light: '#06B6D4',
      dark: '#0E7490',
      surface: '#ECFEFF',
    },
    accent: {
      blue: '#3B82F6',
      blueHover: '#2563EB',
      teal: '#0891B2',
      amber: '#F59E0B',
      rose: '#F43F5E',
      emerald: '#10B981',
    },
    semantic: {
      success: '#10B981',
      successLight: '#D1FAE5',
      successDark: '#059669',
      warning: '#F59E0B',
      warningLight: '#FEF3C7',
      warningDark: '#D97706',
      error: '#EF4444',
      errorLight: '#FEE2E2',
      errorDark: '#DC2626',
      info: '#3B82F6',
      infoLight: '#DBEAFE',
      infoDark: '#2563EB',
    },
    neutral: {
      50: '#FAFBFC',       // Updated: Warmer background
      100: '#F5F6F8',
      200: '#E8EAED',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#525866',      // New: Softer body text
      700: '#3C4251',      // New: Headings
      800: '#1F2937',
      900: '#111827',
    },
    text: {
      primary: '#3C4251',   // Updated: Softer contrast
      secondary: '#6B7280',
      tertiary: '#9CA3AF',
      disabled: '#D1D5DB',
      inverse: '#FFFFFF',
    },
    surface: {
      base: '#FFFFFF',
      elevated: '#FFFFFF',
      sunken: '#F8F9FA',
      overlay: 'rgba(17, 24, 39, 0.5)',
      glass: 'rgba(255, 255, 255, 0.7)',
    },
  },
  spacing: {
    0: 0,
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: 20,
    6: 24,
    8: 32,
    10: 40,
    12: 48,
    16: 64,
    20: 80,
    24: 96,
    // Semantic spacing
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
    xxxl: 64,
  },
  borderRadius: {
    none: 0,
    sm: 4,
    md: 8,
    lg: 12,           // Updated: Default card radius
    xl: 16,
    '2xl': 20,
    '3xl': 24,
    full: 9999,
  },
  shadows: {
    none: 'none',
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.08), 0 1px 2px -1px rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
    // Colored shadows for branding
    purple: '0 8px 24px rgba(59, 130, 246, 0.15)',
    blue: '0 8px 24px rgba(59, 130, 246, 0.15)',
    // Focus shadow
    focus: '0 0 0 3px rgba(59, 130, 246, 0.2)',
  },
  transitions: {
    duration: {
      fast: '150ms',
      normal: '250ms',
      slow: '400ms',
    },
    easing: {
      standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
      decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
      accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  typography: {
    fontFamily: {
      primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
      mono: '"JetBrains Mono", "Fira Code", "Courier New", monospace',
    },
    fontSize: {
      xs: 12,
      sm: 13,
      base: 14,
      md: 14,
      lg: 16,
      xl: 18,
      '2xl': 20,
      '3xl': 24,
      '4xl': 30,
      '5xl': 36,
      '6xl': 48,
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    lineHeight: {
      tight: 1.25,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
      loose: 1.75,
    },
    letterSpacing: {
      tighter: '-0.05em',
      tight: '-0.025em',
      normal: '0',
      wide: '0.025em',
      wider: '0.05em',
      widest: '0.1em',
    },
  },
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
  zIndex: {
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
} as const;

export type Tokens = typeof tokens;
